<template>
  <div class="input">
    <label class="body-smaller-semi-bold">
      {{ labelText }}
    </label>

    <div class="input__container">
      <div
        class="labels-picker"
        @click="focusInput"
        @keydown.delete.stop="onBackspaceDelete">
        <ul class="chips" role="list">
          <li
            v-for="(l, i) in selected"
            :key="l"
            class="chip"
            :title="l">
            <span class="chip-text">{{ l }}</span>
            <button
              type="button"
              class="chip-remove"
              :aria-label="'Remove label ' + l"
              @click.stop="removeLabel(i)">
              ×
            </button>
          </li>
        </ul>

        <input
          ref="textInput"
          v-model="query"
          class="labels-input"
          :placeholder="placeholder"
          aria-autocomplete="list"
          :aria-expanded="showSuggestions"
          aria-haspopup="listbox"
          :aria-activedescendant="activeSuggestionId"
          @focus="isFocused = true"
          @input="onInput"
          @keydown.enter.prevent="addFromQuery"
          @keydown.tab.prevent="addFromQuery"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.delete="onBackspaceDelete"
          @keydown.space.prevent="onSpace"
          @keydown="onKeydown"
          @paste.prevent="onPaste">

        <!-- Suggestions dropdown -->
        <ul v-if="showSuggestions" class="suggestions" role="listbox">
          <li
            v-for="(s, idx) in filteredSuggestions"
            :id="`suggestion-${idx}`"
            :key="s"
            :class="{ 'suggestion-active': idx === highlightedIndex }"
            role="option"
            @mousedown.prevent="selectSuggestion(s)">
            {{ s }}
          </li>
        </ul>

        <input
          type="text"
          :name="name"
          :value="csvValue"
          readonly
          class="csv-hidden-input">
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LabelsPicker',
  props: {
    value: { type: String, default: '' },
    suggestions: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Lägg till etikett...' },
    labelText: { type: String, default: '' },
    name: { type: String, default: 'labels' },
    allowDuplicates: { type: Boolean, default: false }
  },
  data() {
    return {
      selected: [],
      query: '',
      highlightedIndex: -1,
      isFocused: false
    }
  },
  computed: {
    csvValue() {
      return this.selected.join(',')
    },
    filteredSuggestions() {
      if (!this.query) {
        return this.suggestions.filter(
          s => this.allowDuplicates || !this.selected.includes(s)
        )
      }
      const q = this.query.toLowerCase()
      return this.suggestions.filter(
        s =>
          (this.allowDuplicates || !this.selected.includes(s)) &&
            s.toLowerCase().includes(q)
      )
    },
    showSuggestions() {
      return this.isFocused && this.filteredSuggestions.length > 0
    },
    activeSuggestionId() {
      return this.highlightedIndex >= 0
        ? `suggestion-${this.highlightedIndex}`
        : null
    }
  },
  watch: {
    csvValue(val) {
      this.$emit('input', val)
      this.$emit('change', val)
    },
    value: {
      handler(val) {
        const parsed = this.parseCsv(val)
        if (parsed.join(',') !== this.selected.join(',')) {
          this.selected = parsed
        }
      },
      immediate: true
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    parseCsv(csv) {
      if (!csv) return []
      return csv
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    },
    focusInput() {
      this.$refs.textInput && this.$refs.textInput.focus()
      this.isFocused = true
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.isFocused = false
      }
    },
    addLabel(label) {
      const trimmed = label.trim()
      if (!trimmed) return
      if (!this.allowDuplicates && this.selected.includes(trimmed)) return
      this.selected.push(trimmed)
      this.query = ''
      this.highlightedIndex = -1
    },
    removeLabel(index) {
      this.selected.splice(index, 1)
    },
    addFromQuery() {
      if (!this.query) return
      if (
        this.highlightedIndex >= 0 &&
          this.filteredSuggestions[this.highlightedIndex]
      ) {
        this.addLabel(this.filteredSuggestions[this.highlightedIndex])
        this.isFocused = false
        return
      }
      const parts = this.query
        .split(',')
        .map(p => p.trim())
        .filter(Boolean)
      parts.forEach(p => this.addLabel(p))
      this.isFocused = false
    },
    onInput() {
      this.highlightedIndex = -1
    },
    selectSuggestion(s) {
      this.addLabel(s)
      this.isFocused = false
    },
    onSpace() { },
    onPaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData('text')
      const split = text
        .split(/[,\\n\\r]+/)
        .map(s => s.trim())
        .filter(Boolean)
      split.forEach(s => this.addLabel(s))
    },
    onBackspaceDelete() {
      if ((this.query === '' || this.query == null) && this.selected.length > 0) {
        this.selected.pop()
      }
    },
    highlightNext() {
      if (!this.filteredSuggestions.length) return
      this.highlightedIndex =
          (this.highlightedIndex + 1) % this.filteredSuggestions.length
    },
    highlightPrev() {
      if (!this.filteredSuggestions.length) return
      this.highlightedIndex =
          (this.highlightedIndex - 1 + this.filteredSuggestions.length) %
          this.filteredSuggestions.length
    },
    onKeydown(e) {
      if (e.key === ',' || e.key === 'Comma') {
        e.preventDefault()
        this.addFromQuery()
      } else if (e.key === 'Escape') {
        this.isFocused = false
      }
    }
  }
}
</script>

<style scoped>
  .labels-picker {
    border: 1px solid #d1d5db;
    padding: 6px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    position: relative;
    min-height: 40px;
  }

  .chips {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .chip {
    background: rgb(48, 157, 230);
    padding: 4px 8px;
    border-radius: 3px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .chip-text {
    font-size: 0.875rem;
    color: #fff;
  }

  .chip-remove {
    border: none;
    background: transparent;
    cursor: pointer;
    font-weight: bold;
    color: #fff;
  }

  .labels-input {
    border: none;
    outline: none;
    min-width: 120px;
    flex: 1 0 120px;
    padding: 4px;
  }

  .suggestions {
    position: absolute;
    left: 0;
    top: calc(100% + 6px);
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    width: 100%;
    z-index: 50;
    max-height: 220px;
    overflow: auto;
    padding: 6px 0;
  }

    .suggestions li {
      padding: 8px 12px;
      cursor: pointer;
    }

  .suggestion-active {
    background: #eef2ff;
  }

  .csv-hidden-input {
    display: none;
  }
</style>
